// Auth store — Zustand. Persist JWT vào localStorage để keep login giữa reload.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = 'cnvwork-saas-auth';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,                                    // { id, email, display_name }
      activeTenant: null,                            // { tenant_id, org_id, slug, display_name }

      isAuthenticated: () => !!get().accessToken,

      // Fallback to existing nếu field undefined — refresh response chỉ trả {accessToken, user},
      // KHÔNG có refreshToken/active_tenant. Nếu set null/undefined sẽ xóa activeTenant →
      // menu filter mất team_chat (private_modules empty) — đây là bug từng gặp 2026-05-24.
      //
      // QUAN TRỌNG (2026-05-27 fix burst): SHALLOW COMPARE user + activeTenant với
      // cur. Nếu data y hệt → KHÔNG replace identity → child useEffect[user] KHÔNG
      // re-run. Bug trước: axios refresh setSession liên tục → user identity flip
      // → WebPushRegister/TeamChatPushNotifications subscribe loop 40+ lần.
      setSession: ({ accessToken, refreshToken, user, active_tenant }) => {
        const cur = get();
        const sameUser = user && cur.user && cur.user.id === user.id
          && cur.user.role === user.role
          && cur.user.name === user.name
          && cur.user.avatar === user.avatar
          && JSON.stringify(cur.user.permissions || []) === JSON.stringify(user.permissions || []);
        // `company_timezone` (12/10/2026): máy chủ trả ⇒ so luôn — múi đổi thì thay object để lib/workspaceTimezone.js
        // áp múi mới. Vắng khoá (máy chủ cũ) mà vẫn workspace cũ ⇒ GIỮ múi đang có, đừng để một lần làm mới token
        // thả web về giờ VN.
        const sameTenantId = !!active_tenant && !!cur.activeTenant && cur.activeTenant.tenant_id === active_tenant.tenant_id;
        const sameTenant = sameTenantId
          && JSON.stringify(cur.activeTenant.private_modules || {}) === JSON.stringify(active_tenant.private_modules || {})
          && (active_tenant.company_timezone === undefined || active_tenant.company_timezone === cur.activeTenant.company_timezone);
        const nextTenant = active_tenant && sameTenantId && active_tenant.company_timezone === undefined
          ? { ...active_tenant, company_timezone: cur.activeTenant.company_timezone }
          : active_tenant;
        set({
          accessToken,
          refreshToken: refreshToken ?? cur.refreshToken,
          user: user ? (sameUser ? cur.user : user) : cur.user,
          activeTenant: active_tenant ? (sameTenant ? cur.activeTenant : nextTenant) : cur.activeTenant,
        });
      },

      // refreshToken: BE rotate refresh token khi đổi workspace (refresh claim mang
      // active_tenant_id). Vắng field này (token legacy / mất CAS) → giữ token hiện tại.
      setActiveTenant: (active_tenant, accessToken, refreshToken) => set({
        activeTenant: active_tenant,
        accessToken: accessToken ?? get().accessToken,
        refreshToken: refreshToken ?? get().refreshToken,
      }),

      logout: () => set({
        accessToken: null, refreshToken: null, user: null, activeTenant: null,
      }),
    }),
    { name: STORAGE_KEY },
  ),
);

// ─── Sync token giữa các tab (2026-07-25) ────────────────────────────────────────────────
// zustand persist chỉ đọc localStorage LÚC LOAD, không theo dõi thay đổi ⇒ mỗi tab giữ một bản
// token riêng trong bộ nhớ. Với refresh token sliding (BE rotate mỗi lần refresh), tab nền bị
// trình duyệt throttle có thể nằm im rất lâu rồi bắn request bằng token đã lỗi thời → 401 →
// đăng xuất. Nghe `storage` để mọi tab luôn dùng token mới nhất.
//
// ⚠️ WORKSPACE PHẢI ĐI KÈM TOKEN. Access token chính là nguồn tenant ở BE
// (`api/src/middleware/inboxAuth.js`: `req.tenant = claims.active_tenant_id`), FE không gửi
// header tenant nào. Nếu chỉ nuốt token mà giữ nguyên `activeTenant` cũ thì tab này sẽ hiển thị
// workspace Y trong khi MỌI request đọc/ghi chạy trên workspace X — tạo record nhầm workspace.
// Vì vậy: token mang tenant KHÁC tenant tab này đang mở → BỎ QUA hẳn, không nhận token đó.
// (Tab kia đổi workspace là việc của tab kia; tab này giữ nguyên phiên của nó.)
//
// CHỈ xử lý khi có giá trị mới — BỎ QUA sự kiện xoá key, để một tab gặp sự cố không kéo theo
// mọi tab khác cùng văng ra login.
function _tenantOf(token) {
  try {
    const p = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return p?.active_tenant_id ?? null;
  } catch { return null; }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const next = JSON.parse(e.newValue)?.state;
      if (!next?.accessToken) return;
      const cur = useAuthStore.getState();
      if (next.accessToken === cur.accessToken && next.refreshToken === cur.refreshToken) return;
      const nextTenant = _tenantOf(next.accessToken);
      const curTenant = cur.activeTenant?.tenant_id ?? null;
      if (curTenant && nextTenant && nextTenant !== curTenant) return;   // tab kia đổi workspace
      useAuthStore.setState({
        accessToken: next.accessToken,
        refreshToken: next.refreshToken ?? cur.refreshToken,
      });
    } catch { /* JSON hỏng — bỏ qua, tab này vẫn chạy bằng state hiện tại */ }
  });
}
